import React from 'react';
import {
  Alert, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GridColDef } from '@mui/x-data-grid';

import { FDataGrid } from '../grid';
import {
  ISimulationCreateScenarioRequest,
  ISimulationManagerWorkbench,
  ISimulationScenarioRow,
} from './simulation.models';

type Props = {
  workbench: ISimulationManagerWorkbench;
  loading?: boolean;
  onRefresh: () => void | Promise<void>;
  onCreate: (request: ISimulationCreateScenarioRequest) => Promise<void>;
  onOpen: (scenario: ISimulationScenarioRow) => void;
  onDelete: (scenario: ISimulationScenarioRow) => Promise<void>;
};

type State = {
  createOpen: boolean;
  deleteOpen: boolean;
  basisid: number | '';
  forecastbatchid: number | '';
  name: string;
  description: string;
  error: string;
  selectedScenarioId: number | null;
  deleteScenario: ISimulationScenarioRow | null;
};

class SimulationManager extends React.Component<Props, State> {
  state: State = {
    createOpen: false,
    deleteOpen: false,
    basisid: '',
    forecastbatchid: '',
    name: '',
    description: '',
    error: '',
    selectedScenarioId: null,
    deleteScenario: null,
  };

  private openCreate = () => {
    const w = this.props.workbench;
    const basis = (w.basis ?? []).filter((x) => x.active !== false);
    const forecastBasis = basis.find((x) => x.code.toUpperCase() === 'FORECAST') ?? basis[0];
    const batches = w.forecastbatches ?? [];
    const preferredBatch = batches.find((x) => x.rid === w.context.forecastbatchid) ?? batches[0];

    this.setState({
      createOpen: true,
      basisid: forecastBasis?.rid ?? '',
      forecastbatchid: preferredBatch?.rid ?? '',
      name: `${w.context.fiscalyear || ''} Simulation`.trim(),
      description: '',
      error: '',
    });
  };

  private create = async () => {
    const { basisid, forecastbatchid, name, description } = this.state;
    const basis = (this.props.workbench.basis ?? []).find((x) => x.rid === Number(basisid));

    if (basisid === '') return this.setState({ error: 'Scenario basis is required.' });
    if (!name.trim()) return this.setState({ error: 'Scenario name is required.' });
    if (basis?.code.toUpperCase() === 'FORECAST' && forecastbatchid === '') {
      return this.setState({ error: 'A published Forecast Batch is required for a FORECAST scenario.' });
    }

    try {
      await this.props.onCreate({
        planningcycleid: this.props.workbench.context.planningcycleid,
        basisid: Number(basisid),
        forecastbatchid: forecastbatchid === '' ? null : Number(forecastbatchid),
        scenarioname: name.trim(),
        description: description.trim(),
      });
      this.setState({ createOpen: false, error: '' });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private requestDelete = (scenario: ISimulationScenarioRow) => {
    this.setState({ deleteOpen: true, deleteScenario: scenario, error: '' });
  };

  private confirmDelete = async () => {
    const scenario = this.state.deleteScenario;
    if (!scenario) return;
    try {
      await this.props.onDelete(scenario);
      this.setState({ deleteOpen: false, deleteScenario: null, selectedScenarioId: null, error: '' });
    } catch (error: any) {
      this.setState({ error: error?.message || String(error) });
    }
  };

  private columns: GridColDef[] = [
    {
      field: 'nam', headerName: 'Scenario', minWidth: 300, flex: 1,
      renderCell: (p) => (
        <Stack spacing={0.2} sx={{ py: 0.5, overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{p.row.nam}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap>{p.row.shc}</Typography>
        </Stack>
      ),
    },
    { field: 'basiscode', headerName: 'Basis', width: 120, valueGetter: (_v, row: ISimulationScenarioRow) => row.basiscode || 'FORECAST' },
    {
      field: 'forecast', headerName: 'Forecast', minWidth: 210, flex: 0.7,
      valueGetter: (_v, row: ISimulationScenarioRow) => row.forecastbatchname || row.forecastbatchcode || '-',
    },
    {
      field: 'statecode', headerName: 'State', width: 130,
      renderCell: (p) => <Chip size="small" label={p.value || 'DRAFT'} color={String(p.value).toUpperCase() === 'APPROVED' ? 'success' : String(p.value).toUpperCase() === 'SUBMITTED' ? 'info' : 'default'} />,
    },
    { field: 'created', headerName: 'Created', width: 185, valueFormatter: (value) => value ? new Date(String(value)).toLocaleString() : '-' },
    {
      field: 'actions', headerName: '', width: 115, sortable: false, filterable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.25}>
          <Button size="small" onClick={() => this.props.onOpen(p.row as ISimulationScenarioRow)}><OpenInNewIcon fontSize="small" /></Button>
          <Button size="small" color="error" onClick={() => this.requestDelete(p.row as ISimulationScenarioRow)}><DeleteOutlineIcon fontSize="small" /></Button>
        </Stack>
      ),
    },
  ];

  render() {
    const w = this.props.workbench;
    const ctx = w.context;
    //const basis = (w.basis ?? []).filter((x) => x.active !== false);
    const basis = (w.basis ?? []).filter((x) => x.active !== false);
    const batches = w.forecastbatches ?? [];
    const selectedBasis = basis.find((x) => x.rid === Number(this.state.basisid));
    const isForecast = selectedBasis?.code.toUpperCase() === 'FORECAST';

    return (
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="text.secondary">Planning Cycle Forecasts</Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {batches.length ? `${batches.length} published Forecast${batches.length === 1 ? '' : 's'} available` : (ctx.forecastbatchname || ctx.forecastbatchcode || 'No published Forecast available')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}><Typography variant="caption" color="text.secondary">Scenarios</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>{w.summary.scenariocount}</Typography></Grid>
            <Grid size={{ xs: 6, md: 2 }}><Typography variant="caption" color="text.secondary">Draft</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>{w.summary.draftcount}</Typography></Grid>
            <Grid size={{ xs: 6, md: 2 }}><Typography variant="caption" color="text.secondary">Submitted</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>{w.summary.submittedcount}</Typography></Grid>
            <Grid size={{ xs: 6, md: 2 }}><Typography variant="caption" color="text.secondary">Approved</Typography><Typography variant="h6" sx={{ fontWeight: 800 }}>{w.summary.approvedcount}</Typography></Grid>
          </Grid>
        </Paper>

        <Alert severity={w.summary.publishedforecastavailable ? 'info' : 'warning'}>{w.guidance?.message || 'Create or open a Simulation scenario.'}</Alert>

        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} disabled={!w.actions.canCreateScenario || this.props.loading} onClick={this.openCreate}>Create Scenario</Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} disabled={this.props.loading} onClick={this.props.onRefresh}>Refresh</Button>
        </Stack>

        <FDataGrid
          title="Simulation Scenarios"
          subtitle="Planner-owned PLAN scenarios. Internal Objective Gap and Sensitivity workspaces are managed automatically."
          rows={w.scenarios}
          columns={this.columns}
          loading={this.props.loading}
          height={520}
          selectionMode="single"
          showSelectionControl
          selectedRowId={this.state.selectedScenarioId}
          onSelectedRowChange={(row) => this.setState({ selectedScenarioId: row ? Number((row as ISimulationScenarioRow).rid) : null })}
          showQuickFilter
          getRowId={(r: any) => r.rid}
          onRowDoubleClick={(params) => this.props.onOpen(params.row as ISimulationScenarioRow)}
          pageSizeOptions={[10, 25, 50]}
        />

        <Dialog open={this.state.createOpen} onClose={() => this.setState({ createOpen: false })} maxWidth="sm" fullWidth>
          <DialogTitle>Create Simulation Scenario</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              {this.state.error && <Alert severity="error">{this.state.error}</Alert>}
              <Alert severity="info">Create the planner-owned PLAN scenario. Objective Gap and Sensitivity analytical workspaces are created automatically and remain hidden.</Alert>
              <TextField select label="Scenario Basis" value={this.state.basisid} fullWidth onChange={(e) => this.setState({ basisid: Number(e.target.value), forecastbatchid: '' })}>
                {basis.map((b) => <MenuItem key={b.rid} value={b.rid}>{b.name} ({b.code})</MenuItem>)}
              </TextField>
              {isForecast && (
                <TextField select label="Published Forecast" value={this.state.forecastbatchid} fullWidth onChange={(e) => this.setState({ forecastbatchid: Number(e.target.value) })}>
                  {batches.map((b) => <MenuItem key={b.rid} value={b.rid}>{b.name}{b.code ? ` — ${b.code}` : ''}</MenuItem>)}
                </TextField>
              )}
              <TextField label="Scenario Name" value={this.state.name} fullWidth onChange={(e) => this.setState({ name: e.target.value })} />
              <TextField label="Description" value={this.state.description} fullWidth multiline minRows={3} onChange={(e) => this.setState({ description: e.target.value })} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ createOpen: false })}>Cancel</Button>
            <Button variant="contained" onClick={this.create} disabled={this.props.loading || basis.length === 0 || (isForecast && batches.length === 0)}>Create Scenario</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={this.state.deleteOpen} onClose={() => this.setState({ deleteOpen: false, deleteScenario: null })} maxWidth="sm" fullWidth>
          <DialogTitle>Delete Simulation Scenario?</DialogTitle>
          <DialogContent>
            {this.state.error && <Alert severity="error" sx={{ mb: 2 }}>{this.state.error}</Alert>}
            <Alert severity="warning">
              This deletes <strong>{this.state.deleteScenario?.nam}</strong> and its internal Objective Gap and Sensitivity analytical workspaces, including their scenario values, facts and results. The published Forecast is not deleted.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ deleteOpen: false, deleteScenario: null })}>Cancel</Button>
            <Button color="error" variant="contained" onClick={this.confirmDelete} disabled={this.props.loading}>Delete Scenario</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    );
  }
}

export default SimulationManager;
